import { Model, type ModelProps } from '@/infra/model'
import { Id } from '@/infra/id'
import { action, observable } from 'mobx'
import type { ContactAddress } from './ContactAddress'
import {
  BusinessType,
  type IRSBusinessActivityCode,
  type MethodUsedToValueClosingInventory,
} from '@taxfyle/txf-api-client'

export type TaxActivityAssignee =
  | { readonly type: 'TAX_PAYER'; readonly id: string }
  | { readonly type: 'SPOUSE'; readonly id: string }
  | {
      readonly type: 'JOINT'
      readonly taxPayerId: string
      readonly spouseId: string
    }
  | { readonly type: 'UNASSIGNED' }

export type Form1099PaymentsInfo =
  | { readonly didMakePayments: false }
  | { readonly didMakePayments: true; readonly willFileForm1099: boolean }

export type InventoryInfo =
  | { readonly hasInventory: false }
  | {
      readonly hasInventory: true
      readonly methodUsedToValueClosingInventory: MethodUsedToValueClosingInventory | null
    }

export type BusinessActivityProps = ModelProps<BusinessActivity>
export class BusinessActivity extends Model<BusinessActivity> {
  id = Id.none

  @observable accessor archived = false
  @observable accessor businessType: BusinessType = BusinessType.LLC
  @observable accessor businessName: string | null = null
  @observable accessor ein: string | null = null
  @observable accessor principleBusiness: string | null = null
  @observable accessor businessCode: IRSBusinessActivityCode | null = null
  @observable accessor assignedTo: TaxActivityAssignee | null = null

  @observable accessor didNotMateriallyParticipateDuringYear: boolean | null =
    null
  @observable accessor businessWasStartedOrAcquiredDuringYear: boolean | null =
    null
  @observable accessor businessWasDisposedOfDuringYear: boolean | null = null

  @observable accessor carryToForm8960: boolean | null = null
  @observable accessor professionalGambler: boolean | null = null
  @observable accessor exemptNotaryIncome: boolean | null = null
  @observable accessor paperRouteExcludedFromSe: boolean | null = null

  @observable accessor form1099Payments: Form1099PaymentsInfo | null = null
  @observable accessor inventory: InventoryInfo | null = null

  @observable accessor address: ContactAddress | null = null

  @action.bound
  override set({ ...props }: Partial<BusinessActivityProps>): this {
    return super.set(props)
  }

  @action.bound
  archive(): void {
    this.assignedTo = { type: 'UNASSIGNED' }
    this.archived = true
  }

  toProps(): BusinessActivityProps {
    return {
      id: this.id,
      archived: this.archived,
      businessType: this.businessType,
      businessName: this.businessName,
      ein: this.ein,
      principleBusiness: this.principleBusiness,
      businessCode: this.businessCode,
      assignedTo: this.assignedTo,
      didNotMateriallyParticipateDuringYear:
        this.didNotMateriallyParticipateDuringYear,
      businessWasStartedOrAcquiredDuringYear:
        this.businessWasStartedOrAcquiredDuringYear,
      businessWasDisposedOfDuringYear: this.businessWasDisposedOfDuringYear,
      carryToForm8960: this.carryToForm8960,
      professionalGambler: this.professionalGambler,
      exemptNotaryIncome: this.exemptNotaryIncome,
      paperRouteExcludedFromSe: this.paperRouteExcludedFromSe,
      form1099Payments: this.form1099Payments,
      inventory: this.inventory,
      address: this.address,
    }
  }
}
